import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useState, useMemo } from 'react';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { isStatusCodeError } from '@confluence/error-types/entry-points/isStatusCodeError.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';

import { Status } from '../constants/Status.ts';
import type { ClassificationLevel } from '../ClassificationRadioOption.tsx';
import {
	NO_CLASSIFICATION_OPTION,
	NO_CLASSIFICATION_OPTION_ID,
} from '../constants/NoClassification.ts';

import type {
	useDefaultAndSpaceClassificationLevelsQuery$data,
	useDefaultAndSpaceClassificationLevelsQuery$variables,
} from './__generated__/useDefaultAndSpaceClassificationLevelsQuery.graphql.ts';
import type { Color } from '@atlassian/data-classification-level/types';

const UseDefaultAndSpaceClassificationLevelsQuery = graphql`
	query useDefaultAndSpaceClassificationLevelsQuery($spaceKey: String!) {
		space(key: $spaceKey) {
			id
			name
			defaultClassificationLevelId
			metadata {
				totalCurrentPages
			}
		}
		classificationLevels {
			id
			order
			status
			color
			description
			guideline
			guidelineADF
			name
		}
	}
`;

/**
 * Return type for useDefaultAndSpaceClassificationLevels hook. Includes:
 * 1) Classification levels available in a space
   2) Space's default classification
   3) Other space classification metadata (e.g. space name, total pages in space )
 */
export type SpaceClassificationMetadata = {
	classificationLevels: ClassificationLevel[];
	defaultClassification: ClassificationLevel | null | undefined;
	defaultId: string;
	loading: boolean;
	isEnabled: boolean;
	expectedError?: boolean;
	spaceName: string;
	totalCurrentPages: number;
	setUnfilteredClassificationId: Dispatch<string>;
	getClassificationLevelFromId: (
		classificationLevelId?: string | null,
	) => ClassificationLevel | undefined;
};

type UseDefaultAndSpaceClassificationLevelsProps = {
	spaceKey: string;
	isAdmin?: boolean;
	setClassificationsEnabledForSpace?: Dispatch<boolean>;
	skipQuery?: boolean;
	shouldPreloadData?: boolean;
};

/**
 *
 * @param isAdmin
 * @param setClassificationsEnabledForSpace
 * @returns
 * 1) Classification levels available in a space
   2) Space's default classification
   3) Other space classification metadata (e.g. space name, total pages in space )
 * @description
	 Note: an undefined 'isAdmin' will be treated differently than 'false'
 */
export const useDefaultAndSpaceClassificationLevels = ({
	spaceKey,
	isAdmin,
	setClassificationsEnabledForSpace,
	skipQuery = false,
	shouldPreloadData = false,
}: UseDefaultAndSpaceClassificationLevelsProps): {
	classificationLevels: ClassificationLevel[];
	defaultClassification:
		| ClassificationLevel
		| {
				id: string;
				name: string;
				order: number;
				definition: string;
				color: Color;
				status: Status;
		  }
		| undefined;
	defaultId: string;
	loading: boolean;
	isEnabled: boolean;
	expectedError: boolean;
	spaceName: string;
	totalCurrentPages: number;
	setUnfilteredClassificationId: Dispatch<SetStateAction<string>>;
	getClassificationLevelFromId: (
		classificationLevelId?: string | null,
	) => ClassificationLevel | undefined;
} => {
	const { data, loading, error } = useQueryInterop<
		useDefaultAndSpaceClassificationLevelsQuery$data,
		useDefaultAndSpaceClassificationLevelsQuery$variables
	>(UseDefaultAndSpaceClassificationLevelsQuery, {
		variables: { spaceKey },
		skip: !spaceKey || isAdmin === false || skipQuery,
		errorPolicy: 'all',
		fetchPolicy: shouldPreloadData ? 'cache-first' : 'network-only',
	});

	// If there was a 404 status error,
	// then classifications are not enabled for that space
	const isEnabled = !error || (error && !error?.message.includes('Feature is disabled'));
	if (setClassificationsEnabledForSpace) setClassificationsEnabledForSpace(isEnabled);

	// If no default classification is set for the space, then we want
	// to send a fallback No Classification option instead of throwing an error
	const noDefaultClassification = error?.message.includes(
		'No default classification set for space with ID',
	);

	const expectedError =
		(error && !noDefaultClassification && isStatusCodeError(error, '404')) ||
		isStatusCodeError(error, '500') ||
		isStatusCodeError(error, '400');

	// Mark error as handled if 1) feature is not enabled, 2) there is no default
	// classification set, or 3) there was another kind of expected error
	if (!isEnabled || noDefaultClassification || expectedError) {
		markErrorAsHandled(error);
	}

	// Classification levels to display - ordered, and filtered by status
	// (to only display published levels)
	const classificationLevels: ClassificationLevel[] = useMemo(
		() =>
			loading || expectedError || !data
				? []
				: ((data['classificationLevels']
						?.filter((classification) => classification.status === Status.PUBLISHED)
						.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)) ?? []) as ClassificationLevel[]),
		[loading, expectedError, data],
	);

	const defaultId = noDefaultClassification
		? NO_CLASSIFICATION_OPTION_ID
		: (data?.space?.['defaultClassificationLevelId'] ?? '');

	const [unfilteredClassificationId, setUnfilteredClassificationId] = useState(defaultId);

	if (!unfilteredClassificationId && defaultId) {
		setUnfilteredClassificationId(defaultId);
	}

	const spaceName = data?.space?.name ?? '';
	const totalCurrentPages = data?.space?.metadata.totalCurrentPages ?? 0;

	// Find default classification from unfiltered list,
	// in case classification is not published
	const defaultClassification = noDefaultClassification
		? NO_CLASSIFICATION_OPTION
		: (data?.['classificationLevels']?.find((level) => level.id === unfilteredClassificationId) as
				| ClassificationLevel
				| undefined);

	const getClassificationLevelFromId = useCallback(
		(classificationLevelId?: string | null): ClassificationLevel | undefined => {
			return classificationLevels?.find(
				(level: ClassificationLevel) => level.id === classificationLevelId,
			);
		},
		[classificationLevels],
	);

	return {
		classificationLevels,
		defaultClassification,
		defaultId,
		loading,
		isEnabled,
		expectedError,
		spaceName,
		totalCurrentPages,
		setUnfilteredClassificationId,
		getClassificationLevelFromId,
	};
};
